import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { APP_CHART_COLORS } from '../../constants/colors';

interface ChartCardProps {
  title: string;
  data: any[];
  type: 'bar' | 'line' | 'area' | 'pie';
  /** Vertical columns (default) or horizontal bars. Only applies when type is bar. */
  barLayout?: 'vertical' | 'horizontal';
  dataKey: string;
  nameKey: string;
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Hide the top-right menu button. */
  hideMenu?: boolean;
  /** Chart plot height in px (default 300). */
  chartMinHeight?: number;
  /** When true, paper stretches with parent so tall charts fill the cell. */
  fillPaper?: boolean;
  /** Extra styles for the root Paper. */
  paperSx?: SxProps<Theme>;
  /** Pie segment colors (defaults to generic multi-color). */
  segmentColors?: string[];
}

const DEFAULT_PIE_COLORS = [...APP_CHART_COLORS];

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  data,
  type,
  barLayout = 'vertical',
  dataKey,
  nameKey,
  onMenuClick,
  hideMenu = false,
  chartMinHeight,
  fillPaper = false,
  paperSx,
  segmentColors,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const chartH = chartMinHeight ?? 300;
  const pieColors = segmentColors?.length ? segmentColors : DEFAULT_PIE_COLORS;
  const avoidedSeriesLabel = t('carbonAvoidance.avoidedTco2e');

  const formatTimeValue = (value: number) => {
    if (title.includes('TIME')) {
      return `${value.toFixed(1)}h`;
    }
    return value.toString();
  };

  const formatXAxis = (tickItem: string) => {
    if (title.includes('TIME')) {
      // If it's a date string, format it
      if (tickItem.includes('-')) {
        return new Date(tickItem).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    }
    return tickItem;
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 20, right: 30, left: 20, bottom: 5 },
    };

    switch (type) {
      case 'bar':
        if (barLayout === 'horizontal') {
          return (
            <ResponsiveContainer width="100%" height={fillPaper ? '100%' : chartH}>
              <BarChart layout="vertical" data={data} margin={{ top: 12, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
                <XAxis
                  type="number"
                  stroke={theme.palette.text.secondary}
                  tickFormatter={formatTimeValue}
                />
                <YAxis
                  type="category"
                  dataKey={nameKey}
                  width={96}
                  stroke={theme.palette.text.secondary}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value: number) => [formatTimeValue(value), avoidedSeriesLabel]}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey={dataKey} radius={[0, 4, 4, 0]}>
                  {data.map((_, index) => (
                    <Cell
                      key={`bar-cell-${index}`}
                      fill={pieColors[index % pieColors.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          );
        }
        return (
          <ResponsiveContainer width="100%" height={fillPaper ? '100%' : chartH}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey={nameKey} 
                tickFormatter={formatXAxis}
                stroke={theme.palette.text.secondary}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                tickFormatter={formatTimeValue}
              />
              <Tooltip 
                formatter={(value: number) => [formatTimeValue(value), title]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Bar 
                dataKey={dataKey} 
                fill={theme.palette.primary.main}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={chartH}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey={nameKey} 
                tickFormatter={formatXAxis}
                stroke={theme.palette.text.secondary}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                tickFormatter={formatTimeValue}
              />
              <Tooltip 
                formatter={(value: number) => [formatTimeValue(value), title]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{ fill: theme.palette.primary.main, strokeWidth: 2 }}
                activeDot={{ r: 8, fill: theme.palette.primary.main }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={chartH}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey={nameKey} 
                tickFormatter={formatXAxis}
                stroke={theme.palette.text.secondary}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                tickFormatter={formatTimeValue}
              />
              <Tooltip 
                formatter={(value: number) => [formatTimeValue(value), title]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey={dataKey}
                fill={theme.palette.primary.main}
                stroke={theme.palette.primary.dark}
                fillOpacity={0.55}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={chartH}>
            <PieChart>
              <Pie
                data={data}
                dataKey={dataKey}
                nameKey={nameKey}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill={theme.palette.primary.main}
                label={({ name, value }) => `${name}: ${formatTimeValue(value)}`}
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number) => [formatTimeValue(value), title]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <Paper
      sx={{
        p: 3,
        height: fillPaper ? '100%' : 'auto',
        minHeight: fillPaper ? '100%' : undefined,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        bgcolor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
        ...(paperSx ?? {}),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {title}
        </Typography>
        {!hideMenu && onMenuClick && (
          <IconButton
            size="small"
            onClick={onMenuClick}
            sx={{ color: theme.palette.text.secondary }}
          >
            <MoreVertIcon />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: fillPaper ? 0 : chartH,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {renderChart()}
      </Box>
    </Paper>
  );
};

export default ChartCard; 